"use client";

import React, { createContext, useContext } from "react";
import type { BaseEntity } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { DetailActionContext } from "./types";

/**
 * ONE React context serves all 11 entities, so the stored value is erased to
 * the `BaseEntity` instantiation and each reader re-applies its own `T`.
 *
 * The three casts below are IRREDUCIBLE, not laziness — they were checked
 * against the compiler, not assumed:
 *
 * - `DetailActionContext<T>` is INVARIANT in `T`. `item: T` reads it
 *   covariantly, while `onUpdateItem: (updates: Partial<T>) => …`,
 *   `pageConfig.hooks: EntityHooks<T>` and `pageConfig.config:
 *   DetailPageConfig<T>` (whose tab `customComponent`s take
 *   `TabContentProps<T>`) consume it contravariantly. So
 *   `DetailActionContext<Deals>` is neither a subtype nor a supertype of
 *   `DetailActionContext<BaseEntity>`; dropping the provider cast produces
 *   `TS2322: Types of property 'pageConfig' are incompatible`.
 * - A React context value carries no runtime discriminant (no `__typename`
 *   guarantee, no entity tag), so there is nothing for a type guard to narrow
 *   ON. The `T` the provider and reader agree on is a render-tree fact, one
 *   subtree apart — exactly the boundary `getEntityHooks<T>` occupies and is
 *   allow-listed for in ENTITY_HOOKS_USE_CODEGEN_TYPES.
 *
 * Field-level write safety therefore does NOT live here; it lives at the
 * concrete `use<Entity>Hooks()` the provider was built from. Keep the erasure
 * in these three functions and nowhere else.
 */
type ErasedDetailActionContext = DetailActionContext<BaseEntity>;

const DetailActionContextInternal = createContext<ErasedDetailActionContext | null>(null);

interface DetailActionProviderProps<T extends BaseEntity> {
  children: React.ReactNode;
  value: DetailActionContext<T>;
}

export function DetailActionProvider<T extends BaseEntity>({
  children,
  value,
}: DetailActionProviderProps<T>) {
  return (
    <DetailActionContextInternal.Provider value={value as unknown as ErasedDetailActionContext}>
      {children}
    </DetailActionContextInternal.Provider>
  );
}

export function useDetailActionContext<
  T extends BaseEntity = BaseEntity,
>(): DetailActionContext<T> {
  const context = useContext(DetailActionContextInternal);

  if (!context) {
    throw new Error("useDetailActionContext must be used within a DetailActionProvider");
  }

  return context as unknown as DetailActionContext<T>;
}

export function useDetailActionContextOptional<
  T extends BaseEntity = BaseEntity,
>(): DetailActionContext<T> | null {
  const context = useContext(DetailActionContextInternal);
  return context as unknown as DetailActionContext<T> | null;
}
